import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import qs from 'qs'
import { useSelector } from 'react-redux'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import LockIcon from '@mui/icons-material/Lock'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import APIService from 'services/APIService'
import { getCurrentUser } from 'features/auth/ducks/selectors'

export const IDENTITY_FOLDER_ID = 'identity_documents'

export const IDENTITY_FOLDER = {
  id: IDENTITY_FOLDER_ID,
  name: 'Identity documents',
  isVirtual: true
}

export const IDENTITY_LOCKED_TEXT =
  "These are personal ID documents, such as passports and driving licences. To protect people's privacy, only people given identity document access can open them. Ask your administrator if you need access."

export const IDENTITY_NOTE_TEXT = 'Guru does not read these documents and they cannot be shared. Search only matches their names, not the text inside them.'

export const IDENTITY_SEARCH_HINT = 'Search only matches the names of identity documents, not the text inside them.'

const PAGE_SIZE = 50

const api = APIService()

export const identityCountText = (count, buildingCount, showBuildings) => {
  const documents = `${count} document${count === 1 ? '' : 's'}`
  if (!showBuildings) return documents
  return `${documents} across ${buildingCount} building${buildingCount === 1 ? '' : 's'}`
}

const endpointsFor = (context, scopeId) => {
  if (!scopeId) return null
  if (context === 'portfolio-aggregate') {
    return { list: `/portfolios/${scopeId}/aggregate-files`, counts: `/portfolios/${scopeId}/aggregate-files/counts` }
  }
  if (context === 'estate-aggregate') {
    return { list: `/estates/${scopeId}/files`, counts: `/estates/${scopeId}/files/counts` }
  }
  return { list: `/building-passport/${scopeId}/files`, counts: `/building-passport/${scopeId}/files/counts` }
}

export const identityFilterParams = (searchCriteria, sourceBpId) => {
  const params = {}
  const query = (searchCriteria?.query || '').trim()
  if (query.length >= 2) params['filter[search]'] = query
  if (searchCriteria?.fileTypeIds?.length) params['filter[file_type_ids]'] = searchCriteria.fileTypeIds.join(',')
  if (searchCriteria?.uploaderIds?.length) params['filter[uploader_ids]'] = searchCriteria.uploaderIds.join(',')
  if (searchCriteria?.tagIds?.length) params['filter[tag_ids]'] = searchCriteria.tagIds.join(',')
  if (sourceBpId) params['filter[building_passport_id]'] = sourceBpId
  return params
}

const normaliseSummary = summary => {
  if (!summary) return null
  return {
    count: Number(summary.count) || 0,
    buildingCount: Number(summary.buildingCount) || 0,
    canView: Boolean(summary.canView)
  }
}

export const useIdentityDocumentsFolder = ({ context, scopeId, searchCriteria, sourceBpId, countsSummary, active }) => {
  const currentUser = useSelector(getCurrentUser)
  const hideDuplicates = currentUser?.hideDuplicateFiles ?? true
  const mutationVersion = useSelector(state => state.files?.mutationVersion || 0)
  const endpoints = useMemo(() => endpointsFor(context, scopeId), [context, scopeId])
  const filterParams = useMemo(
    () => identityFilterParams(searchCriteria, sourceBpId),
    [searchCriteria, sourceBpId]
  )
  const filterKey = JSON.stringify(filterParams)
  const isFiltered = Object.keys(filterParams).length > 0

  const [filteredSummary, setFilteredSummary] = useState(null)
  const [files, setFiles] = useState([])
  const [cursor, setCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [listSummary, setListSummary] = useState(null)
  const [loadedKey, setLoadedKey] = useState(null)
  const requestSeqRef = useRef(0)

  const baseParams = useCallback(() => {
    const params = { ...filterParams }
    if (hideDuplicates) params['filter[hide_duplicates]'] = 1
    return params
  }, [filterParams, hideDuplicates])

  useEffect(() => {
    if (!endpoints || !isFiltered) {
      setFilteredSummary(null)
      return
    }
    let cancelled = false
    api.get(`${endpoints.counts}${qs.stringify(baseParams(), { addQueryPrefix: true })}`)
      .then(res => { if (!cancelled) setFilteredSummary(normaliseSummary(res?.data?.data?.identityDocuments)) })
      .catch(() => { if (!cancelled) setFilteredSummary(null) })
    return () => { cancelled = true }
  }, [endpoints, isFiltered, baseParams, mutationVersion])

  const fetchPage = useCallback((nextCursor, replace) => {
    if (!endpoints) return
    const seq = ++requestSeqRef.current
    const key = `${filterKey}:${hideDuplicates}`
    setLoading(true)
    const params = {
      ...baseParams(),
      'filter[identity_documents]': 1,
      cursor: nextCursor ?? '',
      limit: PAGE_SIZE
    }
    api.get(`${endpoints.list}${qs.stringify(params, { addQueryPrefix: true })}`)
      .then(res => {
        if (seq !== requestSeqRef.current) return
        const incoming = res?.data?.data || []
        const meta = res?.data?.meta || {}
        setFiles(prev => {
          if (replace) return incoming
          const seen = new Set(prev.map(f => f.id))
          return [...prev, ...incoming.filter(f => !seen.has(f.id))]
        })
        setCursor(meta.nextCursor ?? null)
        setHasMore(Boolean(meta.hasMore))
        setListSummary(normaliseSummary(meta.identityDocuments ? { ...meta.identityDocuments, canView: meta.canView } : null))
        setLoadedKey(key)
      })
      .catch(() => {
        if (seq !== requestSeqRef.current) return
        setHasMore(false)
        setLoadedKey(key)
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false)
      })
  }, [endpoints, filterKey, hideDuplicates, baseParams])

  useEffect(() => {
    if (!active) return
    setFiles([])
    setCursor(null)
    setHasMore(false)
    fetchPage(null, true)
  }, [active, fetchPage, mutationVersion])

  const loadMore = useCallback(() => {
    if (!active || loading || !hasMore || !cursor) return
    fetchPage(cursor, false)
  }, [active, loading, hasMore, cursor, fetchPage])

  const reload = useCallback(() => {
    if (active) fetchPage(null, true)
  }, [active, fetchPage])

  const totalSummary = normaliseSummary(countsSummary)
  const summary = isFiltered
    ? (filteredSummary || (loadedKey === `${filterKey}:${hideDuplicates}` ? listSummary : null))
    : (totalSummary || listSummary)

  return {
    files,
    hasMore,
    loading,
    loadMore,
    reload,
    summary,
    totalSummary,
    isFiltered,
    isLoaded: loadedKey === `${filterKey}:${hideDuplicates}` && !loading
  }
}

export const IdentityFolderIcon = ({ color = '#33becc' }) => (
  <IconWrap aria-hidden='true' style={{ color }}>
    <BadgeOutlinedIcon style={{ fontSize: 17 }} />
    <SmallLock>
      <LockIcon style={{ fontSize: 9 }} />
    </SmallLock>
  </IconWrap>
)

export const IdentityLockedPanel = ({ count = 0, buildingCount = 0, isAggregate = false }) => (
  <LockedPanel role='region' aria-label='Identity documents are locked' data-testid='identity-locked-panel'>
    <LockedIcon>
      <LockOutlinedIcon style={{ fontSize: 28 }} />
    </LockedIcon>
    <LockedTitle>Identity documents are locked</LockedTitle>
    {count !== null && (
      <LockedCount data-testid='identity-locked-count'>
        {identityCountText(count, buildingCount, isAggregate)}
      </LockedCount>
    )}
    <LockedText>{IDENTITY_LOCKED_TEXT}</LockedText>
  </LockedPanel>
)

export const IdentityAccessNote = () => (
  <Note role='note' data-testid='identity-access-note'>
    <InfoOutlinedIcon style={{ fontSize: 18, flexShrink: 0 }} aria-hidden='true' />
    <span>{IDENTITY_NOTE_TEXT}</span>
  </Note>
)

const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 24px;
  height: 20px;
  margin-right: 6px;
  flex-shrink: 0;
`

const SmallLock = styled.span`
  position: absolute;
  right: 0;
  bottom: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
`

const LockedPanel = styled.div`
  margin: 16px auto;
  padding: 32px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 560px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin: 12px 0;
    padding: 24px 16px;
  }
`

const LockedIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #e6f7f9;
  color: #1c8f9b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const LockedTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`

const LockedCount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1c8f9b;
`

const LockedText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: #334155;
`

const Note = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #e6f7f9;
  color: #0f4f56;
  font-size: 15px;
  line-height: 1.45;

  @media (max-width: 768px) {
    margin: 8px 0 0;
  }
`
